import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  type CreateApiKeyRequest,
  type ApiKeyApprovalConfig as GeneratedAPIKeyApprovalConfig,
  type ApiKeyPermissions as GeneratedAPIKeyPermissions,
  type UpdateApiKeyApprovalConfigRequest,
  type UpdateApiKeyRequest,
  apikeyCreateApiKeyMutation,
  apikeyGetApiKeyOptions,
  apikeyGetApiKeyQueryKey,
  apikeyListApiKeysQueryKey,
  apikeyUpdateApiKeyMutation,
} from '@column/api';
import { Button, Dropdown, Icon, Input, Textarea } from '@column/column-ui-kit';
import {
  Badge,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
} from '@column/ui';

import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { FormField, HiddenInput, Loading, PopoverBase, RenderFields, RestrictedBanner } from '~/components';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import PermissionConfiguration, { Mode, PermissionConfigurationSection, useEntitiesAndAccounts } from '~/lib/roles';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import {
  CustomRoleBankAccountLevelOverrides,
  CustomRoleEntityLevelOverrides,
  CustomRolePlatformLevelPermissions,
} from '~/typings/API';
import { keysToCamelCase, keysToSnakeCase } from '~/util/convertKeys';

import { useDeleteAPIKeyAlert } from './_hooks/useDeleteAPIKeyAlert';
import { getErrorMessage, setDefaultOverrides } from './utils';

interface Params {
  id: string;
}

enum Section {
  Transfers = 'api-key-transfers-and-accounts',
  Additional = 'api-key-additional-permissions',
}

enum APIKeyPermissionPreset {
  ViewOnly = 'view-only',
  FullAccess = 'full-access',
  Custom = 'custom',
}

enum APIKeyApprovalPreset {
  NotRequired = 'not-required',
  Required = 'required',
  Custom = 'custom',
}

interface APIKeyFormValues {
  apiKeyToken?: string;
  approvalConfig: APIKeyApprovalConfig;
  description: string;
  name: string;
  permissions: APIKeyPermissions;
  platformId: string;
}

type APIKeySaveValues = Omit<APIKeyFormValues, 'apiKeyToken' | 'approvalConfig'> & {
  approvalConfig: UpdateApiKeyApprovalConfigRequest | null;
};

type UpdateAPIKeyRequestWithNullableClears = Omit<UpdateApiKeyRequest, 'approval_config' | 'permissions'> & {
  approval_config?: UpdateApiKeyApprovalConfigRequest | null;
  permissions?: GeneratedAPIKeyPermissions | null;
};

interface PermissionsPanelSnapshot {
  approvalConfig: APIKeyApprovalConfig;
  approvalPreset: APIKeyApprovalPreset;
  permissions: APIKeyPermissions;
  preset: APIKeyPermissionPreset;
}

interface ApprovalsPanelSnapshot {
  approvalConfig: APIKeyApprovalConfig;
  preset: APIKeyApprovalPreset;
}

interface APIKeyApprovalConfig {
  enabledForAchCredits: boolean;
  enabledForAchDebits: boolean;
  enabledForWire: boolean;
}

type APIKeyGranularPermissionLevel = 'default' | 'none' | 'read' | 'update' | 'write' | 'create-delete';
type APIKeyBankAccountLevelOverrides = Omit<CustomRoleBankAccountLevelOverrides, 'thisBankAccount'> & {
  thisBankAccount?: APIKeyGranularPermissionLevel;
};
type APIKeyEntityLevelOverrides = Omit<CustomRoleEntityLevelOverrides, 'bankAccounts'> & {
  bankAccounts?: APIKeyGranularPermissionLevel;
};
type APIKeyPlatformLevelPermissions = Omit<CustomRolePlatformLevelPermissions, 'bankAccounts'> & {
  bankAccounts?: APIKeyGranularPermissionLevel;
};

interface APIKeyPermissions {
  bankAccountLevelOverrides?: APIKeyBankAccountLevelOverrides[];
  entityLevelOverrides?: APIKeyEntityLevelOverrides[];
  platformLevelPermissions: APIKeyPlatformLevelPermissions;
}

const excludedAPIKeyPermissionKeys = ['apiKeys', 'approveTransfers'] as const;
const includedAPIKeyAdditionalPermissionKeys = ['counterparties', 'loans', 'reporting'] as const;
const apiKeyVisiblePlatformPermissionKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bankAccounts',
  'bookTransfers',
  'counterparties',
  'depositChecks',
  'entities',
  'issueChecks',
  'loans',
  'realtimeTransfers',
  'reporting',
  'stopPayments',
  'wireTransfers',
] as const satisfies readonly (keyof APIKeyPermissions['platformLevelPermissions'])[];
const apiKeyVisibleBankAccountLevelOverrideKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bookTransfers',
  'depositChecks',
  'issueChecks',
  'realtimeTransfers',
  'stopPayments',
  'thisBankAccount',
  'wireTransfers',
] as const;
const apiKeyVisibleEntityLevelOverrideKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bankAccounts',
  'bookTransfers',
  'depositChecks',
  'issueChecks',
  'realtimeTransfers',
  'stopPayments',
  'thisEntity',
  'wireTransfers',
] as const;
const apiKeyApprovalTransferPermissionKeys = ['achCredits', 'achDebits', 'wireTransfers'] as const;

const apiKeyPermissionPresetOptions = [
  {
    icon: <Icon.CircleCheck className="[--icon-color:var(--color-seafoam-600)]!" />,
    label: 'Full Access',
    value: APIKeyPermissionPreset.FullAccess,
  },
  {
    icon: <Icon.Eye className="[--icon-color:var(--color-primary)]!" />,
    label: 'View Only',
    value: APIKeyPermissionPreset.ViewOnly,
  },
  {
    icon: <Icon.Filters className="[--icon-color:var(--color-yellow-600)]!" />,
    label: 'Custom',
    value: APIKeyPermissionPreset.Custom,
  },
];
const apiKeyApprovalPresetOptions = [
  {
    icon: <Icon.Close className="[--icon-color:var(--color-red-600)]!" />,
    label: 'Not Required',
    value: APIKeyApprovalPreset.NotRequired,
  },
  {
    icon: <Icon.CircleCheck className="[--icon-color:var(--color-seafoam-600)]!" />,
    label: 'Required',
    value: APIKeyApprovalPreset.Required,
  },
  {
    icon: <Icon.Filters className="[--icon-color:var(--color-yellow-600)]!" />,
    label: 'Custom',
    value: APIKeyApprovalPreset.Custom,
  },
];
const apiKeySections = [
  { label: 'Transfers and Accounts', value: Section.Transfers },
  { label: 'Additional Permissions', value: Section.Additional },
];

const createDefaultAPIKeyApprovalConfig = (): APIKeyApprovalConfig => ({
  enabledForAchCredits: false,
  enabledForAchDebits: false,
  enabledForWire: false,
});

const createRequiredAPIKeyApprovalConfig = (): APIKeyApprovalConfig => ({
  enabledForAchCredits: true,
  enabledForAchDebits: true,
  enabledForWire: true,
});

const createAPIKeyPlatformLevelPermissions = (
  permissionLevel: 'read' | 'write',
  platformLevelPermissions?: APIKeyPermissions['platformLevelPermissions']
): APIKeyPermissions['platformLevelPermissions'] => {
  return {
    achCredits: platformLevelPermissions?.achCredits ?? permissionLevel,
    achDebits: platformLevelPermissions?.achDebits ?? permissionLevel,
    achReturns: platformLevelPermissions?.achReturns ?? permissionLevel,
    bankAccounts: platformLevelPermissions?.bankAccounts ?? permissionLevel,
    bookTransfers: platformLevelPermissions?.bookTransfers ?? permissionLevel,
    counterparties: platformLevelPermissions?.counterparties ?? permissionLevel,
    depositChecks: platformLevelPermissions?.depositChecks ?? permissionLevel,
    entities: platformLevelPermissions?.entities ?? permissionLevel,
    issueChecks: platformLevelPermissions?.issueChecks ?? permissionLevel,
    loans: platformLevelPermissions?.loans ?? permissionLevel,
    realtimeTransfers: platformLevelPermissions?.realtimeTransfers ?? permissionLevel,
    reporting: platformLevelPermissions?.reporting ?? 'read',
    stopPayments: platformLevelPermissions?.stopPayments ?? permissionLevel,
    wireTransfers: platformLevelPermissions?.wireTransfers ?? permissionLevel,
  };
};

const createDefaultAPIKeyPermissions = (): APIKeyPermissions => ({
  bankAccountLevelOverrides: [],
  entityLevelOverrides: [],
  platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write'),
});

const createPresetAPIKeyPermissions = (permissionLevel: 'read' | 'write', platformId?: string): APIKeyFormValues => ({
  ...defaultFormValues(platformId),
  permissions: {
    bankAccountLevelOverrides: [],
    entityLevelOverrides: [],
    platformLevelPermissions: createAPIKeyPlatformLevelPermissions(permissionLevel),
  },
});

const defaultFormValues = (platformId?: string): APIKeyFormValues => ({
  apiKeyToken: '',
  approvalConfig: createDefaultAPIKeyApprovalConfig(),
  description: '',
  name: '',
  permissions: createDefaultAPIKeyPermissions(),
  platformId: platformId ?? '',
});

const getAPIKeyPermissionPreset = (
  platformLevelPermissions?: APIKeyPermissions['platformLevelPermissions']
): APIKeyPermissionPreset => {
  if (
    platformLevelPermissions &&
    apiKeyVisiblePlatformPermissionKeys.every((permission) => platformLevelPermissions[permission] === 'read')
  ) {
    return APIKeyPermissionPreset.ViewOnly;
  }
  if (
    platformLevelPermissions &&
    apiKeyVisiblePlatformPermissionKeys.every((permission) =>
      permission === 'reporting'
        ? platformLevelPermissions[permission] === 'read'
        : platformLevelPermissions[permission] === 'write'
    )
  ) {
    return APIKeyPermissionPreset.FullAccess;
  }
  return APIKeyPermissionPreset.Custom;
};

const getAPIKeyPermissionPresetFromPermissions = (permissions: APIKeyPermissions): APIKeyPermissionPreset => {
  if ((permissions.bankAccountLevelOverrides?.length ?? 0) > 0 || (permissions.entityLevelOverrides?.length ?? 0) > 0) {
    return APIKeyPermissionPreset.Custom;
  }

  return getAPIKeyPermissionPreset(permissions.platformLevelPermissions);
};

const apiKeyPermissionsToForm = (permissions: APIKeyPermissions | GeneratedAPIKeyPermissions): APIKeyPermissions =>
  keysToCamelCase(permissions) as APIKeyPermissions;

const apiKeyPermissionsToRequest = (permissions: APIKeyPermissions): GeneratedAPIKeyPermissions =>
  keysToSnakeCase(permissions) as GeneratedAPIKeyPermissions;

const apiKeyApprovalConfigToForm = (
  approvalConfig: APIKeyApprovalConfig | GeneratedAPIKeyApprovalConfig
): APIKeyApprovalConfig => keysToCamelCase(approvalConfig) as APIKeyApprovalConfig;

const apiKeyApprovalConfigToRequest = (approvalConfig: APIKeyApprovalConfig): UpdateApiKeyApprovalConfigRequest =>
  keysToSnakeCase(approvalConfig) as UpdateApiKeyApprovalConfigRequest;

const normalizeAPIKeyGranularPermissionLevelForRequest = (
  permissionLevel?: APIKeyGranularPermissionLevel
): APIKeyGranularPermissionLevel | undefined => (permissionLevel === 'create-delete' ? 'write' : permissionLevel);

const normalizeAPIKeyPermissionsForRequest = (permissions: APIKeyPermissions): APIKeyPermissions => ({
  bankAccountLevelOverrides: permissions.bankAccountLevelOverrides?.map((override) => ({
    ...override,
    thisBankAccount: normalizeAPIKeyGranularPermissionLevelForRequest(override.thisBankAccount),
  })),
  entityLevelOverrides: permissions.entityLevelOverrides?.map((override) => ({
    ...override,
    bankAccounts: normalizeAPIKeyGranularPermissionLevelForRequest(override.bankAccounts),
  })),
  platformLevelPermissions: {
    ...permissions.platformLevelPermissions,
    bankAccounts: normalizeAPIKeyGranularPermissionLevelForRequest(permissions.platformLevelPermissions.bankAccounts),
  },
});

const normalizeAPIKeyPermissions = (
  permissions?: APIKeyPermissions | GeneratedAPIKeyPermissions | null
): APIKeyPermissions => {
  const formPermissions = permissions ? apiKeyPermissionsToForm(permissions) : undefined;

  return {
    bankAccountLevelOverrides: setDefaultOverrides(
      formPermissions?.bankAccountLevelOverrides,
      'bankAccountId',
      apiKeyVisibleBankAccountLevelOverrideKeys
    ),
    entityLevelOverrides: setDefaultOverrides(
      formPermissions?.entityLevelOverrides,
      'entityId',
      apiKeyVisibleEntityLevelOverrideKeys
    ),
    platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write', formPermissions?.platformLevelPermissions),
  };
};

const sanitizeAPIKeyPermissions = (permissions: APIKeyPermissions): APIKeyPermissions => ({
  bankAccountLevelOverrides: setDefaultOverrides(
    permissions.bankAccountLevelOverrides,
    'bankAccountId',
    apiKeyVisibleBankAccountLevelOverrideKeys
  ),
  entityLevelOverrides: setDefaultOverrides(
    permissions.entityLevelOverrides,
    'entityId',
    apiKeyVisibleEntityLevelOverrideKeys
  ),
  platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write', permissions.platformLevelPermissions),
});

const getPermissionSavePayload = (
  preset: APIKeyPermissionPreset,
  permissions: APIKeyPermissions
): GeneratedAPIKeyPermissions | null =>
  preset === APIKeyPermissionPreset.FullAccess
    ? null
    : apiKeyPermissionsToRequest(normalizeAPIKeyPermissionsForRequest(sanitizeAPIKeyPermissions(permissions)));

const getAPIKeyApprovalPresetFromConfig = (approvalConfig: APIKeyApprovalConfig): APIKeyApprovalPreset =>
  approvalConfig.enabledForAchCredits && approvalConfig.enabledForAchDebits && approvalConfig.enabledForWire
    ? APIKeyApprovalPreset.Required
    : approvalConfig.enabledForAchCredits || approvalConfig.enabledForAchDebits || approvalConfig.enabledForWire
      ? APIKeyApprovalPreset.Custom
      : APIKeyApprovalPreset.NotRequired;

const getApprovalConfigSavePayload = (
  preset: APIKeyApprovalPreset,
  approvalConfig: APIKeyApprovalConfig
): UpdateApiKeyApprovalConfigRequest | null =>
  preset === APIKeyApprovalPreset.NotRequired ? null : apiKeyApprovalConfigToRequest(approvalConfig);

const copyAPIKeyPermissions = (permissions: APIKeyPermissions): APIKeyPermissions => ({
  bankAccountLevelOverrides: (permissions.bankAccountLevelOverrides ?? []).map((override) => ({ ...override })),
  entityLevelOverrides: (permissions.entityLevelOverrides ?? []).map((override) => ({ ...override })),
  platformLevelPermissions: { ...permissions.platformLevelPermissions },
});

const copyAPIKeyApprovalConfig = (approvalConfig: APIKeyApprovalConfig): APIKeyApprovalConfig => ({
  ...approvalConfig,
});

const canAPIKeyCreateTransfers = (permissions?: APIKeyPermissions): boolean => {
  const hasWritePermission = (
    permissionSet?: Partial<Record<(typeof apiKeyApprovalTransferPermissionKeys)[number], unknown>>
  ) => apiKeyApprovalTransferPermissionKeys.some((permission) => permissionSet?.[permission] === 'write');

  return (
    hasWritePermission(permissions?.platformLevelPermissions) ||
    (permissions?.entityLevelOverrides ?? []).some(hasWritePermission) ||
    (permissions?.bankAccountLevelOverrides ?? []).some(hasWritePermission)
  );
};

const getAPIKeySaveValues = (data: APIKeyFormValues, approvalPreset: APIKeyApprovalPreset): APIKeySaveValues => ({
  approvalConfig: getApprovalConfigSavePayload(approvalPreset, data.approvalConfig),
  description: data.description,
  name: data.name,
  permissions: data.permissions,
  platformId: data.platformId,
});

const serializeAPIKeySaveValues = (values: APIKeySaveValues): string => JSON.stringify(values);

const getAPIErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorRecord = error as { code?: unknown };

  if (typeof errorRecord.code === 'string') {
    return errorRecord.code;
  }

  return undefined;
};

export const PageApiKeysEdit: React.FC = () => {
  const { currentUser, currentPlatform, isAuthorized, isSandbox } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const apiKeyPermissionsEnabled = useFeatureFlag(FeatureFlag.EnableAPIKeyPermissions);
  const queryClient = useQueryClient();
  const [apiKeyName, setApiKeyName] = useState<string>('');
  const [visibleSection, setVisibleSection] = useState<Section>(Section.Transfers);
  const [isPermissionsPanelOpen, setIsPermissionsPanelOpen] = useState<boolean>(false);
  const [isApprovalsPanelOpen, setIsApprovalsPanelOpen] = useState<boolean>(false);
  const [savedAPIKeySaveValues, setSavedAPIKeySaveValues] = useState<APIKeySaveValues | null>(null);
  const [selectedPermissionPreset, setSelectedPermissionPreset] = useState<APIKeyPermissionPreset>(
    APIKeyPermissionPreset.FullAccess
  );
  const [selectedApprovalPreset, setSelectedApprovalPreset] = useState<APIKeyApprovalPreset>(
    APIKeyApprovalPreset.NotRequired
  );

  const formMethods = useForm<APIKeyFormValues>({
    defaultValues: defaultFormValues(currentPlatform?.id ?? currentUser?.defaultPlatformId),
  });
  const { control, getValues, handleSubmit, reset, setValue } = formMethods;

  const permissionsPanelSnapshot = useRef<PermissionsPanelSnapshot | null>(null);
  const approvalsPanelSnapshot = useRef<ApprovalsPanelSnapshot | null>(null);

  const formValues = useWatch({ control }) as APIKeyFormValues;
  const bankAccountLevelOverrides = useWatch({ control, name: 'permissions.bankAccountLevelOverrides' });
  const entityLevelOverrides = useWatch({ control, name: 'permissions.entityLevelOverrides' });

  const selectedMode = isSandbox ? Mode.Sandbox : Mode.Live;
  const platformId = currentPlatform?.id ?? currentUser?.defaultPlatformId ?? '';
  const apiKeyQuery = useQuery({
    ...apikeyGetApiKeyOptions({
      path: { api_key_id: id ?? '', platform_id: platformId },
    }),
    enabled: !!id && !!platformId,
    retry: (failureCount, error) => getAPIErrorCode(error) !== 'invalid_resource_id_format' && failureCount < 3,
  });
  const createAPIKey = useMutation(apikeyCreateApiKeyMutation());
  const updateAPIKey = useMutation(apikeyUpdateApiKeyMutation());
  const openDeleteAPIKeyAlert = useDeleteAPIKeyAlert({
    platformId,
    redirectOnDelete: true,
  });

  const existingBankAccountIDs = useMemo(
    () =>
      (bankAccountLevelOverrides ?? [])
        .map((bankAccount) => bankAccount.bankAccountId)
        .filter((bankAccountId): bankAccountId is string => typeof bankAccountId === 'string'),
    [bankAccountLevelOverrides]
  );
  const existingEntityIDs = useMemo(
    () =>
      (entityLevelOverrides ?? [])
        .map((entity) => entity.entityId)
        .filter((entityId): entityId is string => typeof entityId === 'string'),
    [entityLevelOverrides]
  );

  const sandboxEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    enabled: apiKeyPermissionsEnabled,
    entityIds: existingEntityIDs,
    mode: Mode.Sandbox,
  });
  const liveEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    enabled: apiKeyPermissionsEnabled,
    entityIds: existingEntityIDs,
    mode: Mode.Live,
  });

  useEffect(() => {
    if (!apiKeyPermissionsEnabled) {
      setIsPermissionsPanelOpen(false);
      setIsApprovalsPanelOpen(false);
      permissionsPanelSnapshot.current = null;
      approvalsPanelSnapshot.current = null;
    }
  }, [apiKeyPermissionsEnabled]);

  const openPermissionsPanel = useCallback(() => {
    permissionsPanelSnapshot.current = {
      approvalConfig: copyAPIKeyApprovalConfig(getValues('approvalConfig') ?? createDefaultAPIKeyApprovalConfig()),
      approvalPreset: selectedApprovalPreset,
      permissions: copyAPIKeyPermissions({
        bankAccountLevelOverrides: getValues('permissions.bankAccountLevelOverrides') ?? [],
        entityLevelOverrides: getValues('permissions.entityLevelOverrides') ?? [],
        platformLevelPermissions:
          getValues('permissions.platformLevelPermissions') ??
          createDefaultAPIKeyPermissions().platformLevelPermissions,
      }),
      preset: selectedPermissionPreset,
    };
    setIsPermissionsPanelOpen(true);
  }, [getValues, selectedApprovalPreset, selectedPermissionPreset]);

  const closePermissionsPanel = useCallback(() => {
    setIsPermissionsPanelOpen(false);
    permissionsPanelSnapshot.current = null;
  }, []);

  const submitPermissionsPanel = useCallback(() => {
    setSelectedPermissionPreset(
      getAPIKeyPermissionPresetFromPermissions(sanitizeAPIKeyPermissions(getValues('permissions')))
    );
    closePermissionsPanel();
  }, [closePermissionsPanel, getValues]);

  const cancelPermissionsPanel = useCallback(() => {
    const snapshot = permissionsPanelSnapshot.current;

    if (snapshot) {
      setSelectedPermissionPreset(snapshot.preset);
      setSelectedApprovalPreset(snapshot.approvalPreset);
      setValue('approvalConfig', snapshot.approvalConfig, { shouldDirty: true });
      setValue('permissions.bankAccountLevelOverrides', snapshot.permissions.bankAccountLevelOverrides, {
        shouldDirty: true,
      });
      setValue('permissions.entityLevelOverrides', snapshot.permissions.entityLevelOverrides, { shouldDirty: true });
      setValue('permissions.platformLevelPermissions', snapshot.permissions.platformLevelPermissions, {
        shouldDirty: true,
      });
    }

    closePermissionsPanel();
  }, [closePermissionsPanel, setValue]);

  const handlePermissionPresetChange = useCallback(
    (preset: APIKeyPermissionPreset) => {
      setSelectedPermissionPreset(preset);

      if (preset === APIKeyPermissionPreset.Custom) {
        setValue(
          'permissions.platformLevelPermissions',
          getValues('permissions.platformLevelPermissions') ??
            createDefaultAPIKeyPermissions().platformLevelPermissions,
          { shouldDirty: true, shouldTouch: true }
        );
        openPermissionsPanel();
        return;
      }

      const permissionLevel = preset === APIKeyPermissionPreset.ViewOnly ? 'read' : 'write';
      const presetPermissions = createPresetAPIKeyPermissions(permissionLevel, platformId);

      setIsPermissionsPanelOpen(false);
      setValue('permissions.bankAccountLevelOverrides', [], { shouldDirty: true, shouldTouch: true });
      setValue('permissions.entityLevelOverrides', [], { shouldDirty: true, shouldTouch: true });
      setValue('permissions.platformLevelPermissions', presetPermissions.permissions.platformLevelPermissions, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [getValues, openPermissionsPanel, platformId, setValue]
  );

  const openApprovalsPanel = useCallback(() => {
    approvalsPanelSnapshot.current = {
      approvalConfig: copyAPIKeyApprovalConfig(getValues('approvalConfig') ?? createDefaultAPIKeyApprovalConfig()),
      preset: selectedApprovalPreset,
    };
    setIsApprovalsPanelOpen(true);
  }, [getValues, selectedApprovalPreset]);

  const closeApprovalsPanel = useCallback(() => {
    setIsApprovalsPanelOpen(false);
    approvalsPanelSnapshot.current = null;
  }, []);

  const submitApprovalsPanel = useCallback(() => {
    setSelectedApprovalPreset(getAPIKeyApprovalPresetFromConfig(getValues('approvalConfig')));
    closeApprovalsPanel();
  }, [closeApprovalsPanel, getValues]);

  const cancelApprovalsPanel = useCallback(() => {
    const snapshot = approvalsPanelSnapshot.current;

    if (snapshot) {
      setSelectedApprovalPreset(snapshot.preset);
      setValue('approvalConfig', snapshot.approvalConfig, { shouldDirty: true });
    }

    closeApprovalsPanel();
  }, [closeApprovalsPanel, setValue]);

  const handleApprovalPresetChange = useCallback(
    (preset: APIKeyApprovalPreset) => {
      setSelectedApprovalPreset(preset);

      if (preset === APIKeyApprovalPreset.Custom) {
        setValue('approvalConfig', getValues('approvalConfig') ?? createDefaultAPIKeyApprovalConfig(), {
          shouldDirty: true,
          shouldTouch: true,
        });
        openApprovalsPanel();
        return;
      }

      setIsApprovalsPanelOpen(false);
      setValue(
        'approvalConfig',
        preset === APIKeyApprovalPreset.Required
          ? createRequiredAPIKeyApprovalConfig()
          : createDefaultAPIKeyApprovalConfig(),
        { shouldDirty: true, shouldTouch: true }
      );
    },
    [getValues, openApprovalsPanel, setValue]
  );

  useBreadcrumbTitle(id ? apiKeyName : 'Create');

  useEffect(() => {
    if (!id) {
      setSelectedPermissionPreset(APIKeyPermissionPreset.FullAccess);
      setSelectedApprovalPreset(APIKeyApprovalPreset.NotRequired);
      setSavedAPIKeySaveValues(null);
      reset(defaultFormValues(platformId));
      return;
    }

    if (!apiKeyQuery.data) {
      return;
    }

    const response = apiKeyQuery.data;
    const responsePermissions = response.permissions
      ? normalizeAPIKeyPermissions(response.permissions)
      : createDefaultAPIKeyPermissions();
    const responseApprovalConfig = response.approval_config
      ? apiKeyApprovalConfigToForm(response.approval_config)
      : createDefaultAPIKeyApprovalConfig();
    const nextFormValues: APIKeyFormValues = {
      ...defaultFormValues(platformId),
      apiKeyToken: response.api_key_token,
      approvalConfig: responseApprovalConfig,
      description: response.description,
      name: response.name,
      permissions: responsePermissions,
    };
    const nextPermissionPreset = response.permissions
      ? getAPIKeyPermissionPresetFromPermissions(responsePermissions)
      : APIKeyPermissionPreset.FullAccess;
    const nextApprovalPreset = response.approval_config
      ? getAPIKeyApprovalPresetFromConfig(responseApprovalConfig)
      : APIKeyApprovalPreset.NotRequired;

    setApiKeyName(response.name);
    setSelectedPermissionPreset(nextPermissionPreset);
    setSelectedApprovalPreset(nextApprovalPreset);
    setSavedAPIKeySaveValues(getAPIKeySaveValues(nextFormValues, nextApprovalPreset));
    reset(nextFormValues);
  }, [apiKeyQuery.data, id, platformId, reset]);

  useEffect(() => {
    if (!id || !apiKeyQuery.error) {
      return;
    }

    navigate(ROUTE.PLATFORM_API_KEYS);
    if (getAPIErrorCode(apiKeyQuery.error) === 'invalid_resource_id_format') {
      addDangerNotification({
        content: 'API key not found',
      });
      return;
    }

    addDangerNotification({
      content: getErrorMessage(apiKeyQuery.error),
    });
  }, [addDangerNotification, apiKeyQuery.error, id, navigate]);

  const hasUnsavedChanges = useMemo(() => {
    if (!id) {
      return true;
    }

    if (!savedAPIKeySaveValues) {
      return false;
    }

    return (
      serializeAPIKeySaveValues(getAPIKeySaveValues(formValues, selectedApprovalPreset)) !==
      serializeAPIKeySaveValues(savedAPIKeySaveValues)
    );
  }, [formValues, id, savedAPIKeySaveValues, selectedApprovalPreset]);

  const canWriteAPIKeys = isAuthorized({ permission: 'apiKeys', permissionLevel: 'write' });
  const apiKeyCanCreateTransfers = canAPIKeyCreateTransfers(formValues.permissions);
  const toggleApprovalConfigField = useCallback(
    (
      field:
        | 'approvalConfig.enabledForAchCredits'
        | 'approvalConfig.enabledForAchDebits'
        | 'approvalConfig.enabledForWire'
    ) => {
      setValue(field, !getValues(field), { shouldDirty: true, shouldTouch: true });
    },
    [getValues, setValue]
  );
  const handleApprovalConfigCellKeyDown = useCallback(
    (
      event: React.KeyboardEvent<HTMLDivElement>,
      field:
        | 'approvalConfig.enabledForAchCredits'
        | 'approvalConfig.enabledForAchDebits'
        | 'approvalConfig.enabledForWire'
    ) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      toggleApprovalConfigField(field);
    },
    [toggleApprovalConfigField]
  );

  useEffect(() => {
    if (!apiKeyCanCreateTransfers) {
      setIsApprovalsPanelOpen(false);
      setSelectedApprovalPreset(APIKeyApprovalPreset.NotRequired);
      setValue('approvalConfig', createDefaultAPIKeyApprovalConfig(), { shouldDirty: true, shouldTouch: true });
      approvalsPanelSnapshot.current = null;
    }
  }, [apiKeyCanCreateTransfers, setValue]);

  const handleSave = (data: APIKeyFormValues) => {
    if (!currentUser) {
      return;
    }

    const saveValues = getAPIKeySaveValues(data, selectedApprovalPreset);
    const permissionSavePayload = getPermissionSavePayload(selectedPermissionPreset, saveValues.permissions);
    const permissions = apiKeyPermissionsEnabled ? permissionSavePayload : undefined;
    const approvalConfig = !apiKeyPermissionsEnabled
      ? undefined
      : apiKeyCanCreateTransfers
        ? saveValues.approvalConfig
        : null;

    if (id) {
      const updateBody: UpdateAPIKeyRequestWithNullableClears = {
        description: saveValues.description,
        name: saveValues.name,
        ...(typeof permissions !== 'undefined' ? { permissions } : {}),
        ...(typeof approvalConfig !== 'undefined' ? { approval_config: approvalConfig } : {}),
      };

      updateAPIKey
        .mutateAsync({
          body: updateBody as UpdateApiKeyRequest,
          path: { api_key_id: id, platform_id: saveValues.platformId },
        })
        .then(() => {
          setApiKeyName(saveValues.name);
          setSavedAPIKeySaveValues(saveValues);
          reset(data);
          queryClient.invalidateQueries({
            queryKey: apikeyGetApiKeyQueryKey({
              path: { api_key_id: id, platform_id: saveValues.platformId },
            }),
          });
          queryClient.invalidateQueries({
            queryKey: apikeyListApiKeysQueryKey({
              path: { platform_id: saveValues.platformId },
            }),
          });
          addSuccessNotification({
            content: 'API key updated',
          });
        })
        .catch((error) => {
          addDangerNotification({
            content: getErrorMessage(error),
          });
        });
      return;
    }

    const createBody = {
      description: saveValues.description,
      name: saveValues.name,
      platform_id: saveValues.platformId,
    };
    const createRequestBody: CreateApiKeyRequest = {
      ...createBody,
      ...(permissions !== null && typeof permissions !== 'undefined' ? { permissions } : {}),
      ...(approvalConfig !== null && typeof approvalConfig !== 'undefined' ? { approval_config: approvalConfig } : {}),
    };

    createAPIKey
      .mutateAsync({
        body: createRequestBody,
      })
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: apikeyListApiKeysQueryKey({
            path: { platform_id: saveValues.platformId },
          }),
        });
        navigate(`${ROUTE.PLATFORM_API_KEYS}/edit/${response.id}`);
        addSuccessNotification({
          content: 'API key created',
        });
      })
      .catch((error) => {
        addDangerNotification({
          content: getErrorMessage(error),
        });
      });
  };
  const handleCancel = useCallback(() => {
    if (id) {
      navigate(ROUTE.PLATFORM_API_KEYS);
      return;
    }
    navigate(-1);
  }, [id, navigate]);
  const isEditLoading = !!id && apiKeyQuery.isLoading;

  const deleteAction = id ? (
    <Button
      isDisabled={!canWriteAPIKeys}
      onClick={() => openDeleteAPIKeyAlert({ id, name: apiKeyName })}
      size="small"
      variant="secondary-danger"
    >
      Delete API Key
    </Button>
  ) : null;
  const backAction = (
    <Button onClick={handleCancel} variant="secondary" size="small">
      Cancel
    </Button>
  );
  const saveAction = (
    <Button onClick={handleSubmit(handleSave)} isDisabled={!canWriteAPIKeys} size="small" icon={!id && <Icon.Plus />}>
      {id ? 'Save API Key' : 'Create API Key'}
    </Button>
  );
  const headerDecoration = currentPlatform ? (
    <Badge
      variant="outline"
      className={cn(
        'ml-1 translate-y-px rounded-md',
        isSandbox
          ? 'border-orange-700/10 bg-orange-200/50 text-orange-600/80 dark:border-orange-400/20 dark:bg-orange-500/15 dark:text-orange-300'
          : 'border-seafoam-700/10 bg-seafoam-100/50 text-seafoam-700 dark:border-seafoam-400/20 dark:bg-seafoam-500/15 dark:text-seafoam-300'
      )}
    >
      {isSandbox ? 'Sandbox' : 'Live'}
    </Badge>
  ) : null;

  const actions =
    id && !hasUnsavedChanges ? (
      <PageActions key="api-key-back-action" headerDecoration={headerDecoration}>
        {deleteAction}
        {backAction}
      </PageActions>
    ) : (
      <PageActions key="api-key-save-actions" headerDecoration={headerDecoration}>
        {deleteAction}
        {backAction}
        {saveAction}
      </PageActions>
    );

  return (
    <>
      {actions}
      <Loading isLoading={isEditLoading}>
        {!canWriteAPIKeys && (
          <Inner pb={0}>
            <RestrictedBanner />
          </Inner>
        )}
        <FormProvider {...formMethods}>
          <form autoComplete="off" onSubmit={handleSubmit(handleSave)}>
            {RenderFields<APIKeyFormValues>({
              sections: [
                {
                  fields: [
                    {
                      id: 'name',
                      label: 'Name',
                      rules: { required: true },
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                        return (
                          <Input
                            value={String(value)}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            placeholder="Name"
                            hasError={isTouched && !!error}
                          />
                        );
                      },
                    },
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                        return (
                          <Textarea
                            value={value !== undefined ? String(value) : ''}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            placeholder="Description"
                            hasError={isTouched && !!error}
                          />
                        );
                      },
                    },
                    ...(apiKeyPermissionsEnabled
                      ? [
                          {
                            id: 'permissions.platformLevelPermissions' as const,
                            label: 'Permissions',
                            children: () => (
                              <>
                                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                                  <Dropdown
                                    active={selectedPermissionPreset}
                                    options={apiKeyPermissionPresetOptions}
                                    onChange={(preset) =>
                                      handlePermissionPresetChange(preset as APIKeyPermissionPreset)
                                    }
                                    fullWidth
                                  />
                                  {selectedPermissionPreset === APIKeyPermissionPreset.Custom && (
                                    <Button
                                      className="h-10"
                                      type="button"
                                      variant="secondary"
                                      size="small"
                                      icon={<Icon.Edit />}
                                      onClick={openPermissionsPanel}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                </div>
                              </>
                            ),
                          },
                          {
                            id: 'approvalConfig.enabledForAchCredits' as const,
                            label: 'Transfer Approvals',
                            description: 'Configure whether transfers sent by this key require approval.',
                            hide: !apiKeyCanCreateTransfers,
                            children: () => (
                              <div className="grid gap-1.5">
                                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                                  <Dropdown
                                    active={selectedApprovalPreset}
                                    options={apiKeyApprovalPresetOptions}
                                    onChange={(preset) => handleApprovalPresetChange(preset as APIKeyApprovalPreset)}
                                    fullWidth
                                  />
                                  {selectedApprovalPreset === APIKeyApprovalPreset.Custom && (
                                    <Button
                                      className="h-10"
                                      type="button"
                                      variant="secondary"
                                      size="small"
                                      icon={<Icon.Edit />}
                                      onClick={openApprovalsPanel}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                </div>
                                {selectedApprovalPreset === APIKeyApprovalPreset.Required && (
                                  <p className="m-0 text-xs leading-4 text-muted-foreground">
                                    Approvals are only supported for ACH and Wire transfers.
                                  </p>
                                )}
                              </div>
                            ),
                          },
                        ]
                      : []),
                  ],
                },
                {
                  fields: [
                    {
                      id: 'apiKeyToken',
                      label: 'API Key',
                      children: ({ value }) => {
                        return (
                          <HiddenInput
                            value={value === undefined ? '' : String(value)}
                            placeholder="Key Will be Generated"
                          />
                        );
                      },
                    },
                  ],
                },
              ],
            })}
            {apiKeyPermissionsEnabled && (
              <PopoverBase
                show={isPermissionsPanelOpen}
                usePortal
                withoutBackdrop
                className={cn(
                  '![--animation-enter-x:24px] ![--animation-exit-x:24px] ![--animation-enter-y:0px] ![--animation-exit-y:0px]',
                  '[--popover-left:-24px] [--popover-padding:0] [--popover-position:fixed] [--popover-right:0] [--popover-top:0px] [--popover-width:min(100vw,700px)]',
                  'fixed! top-0! right-0! bottom-0! left-[-24px]! z-[14]! flex! h-dvh! w-[calc(100vw+24px)]! justify-end! overflow-hidden! bg-[rgba(18,18,22,0.56)]',
                  '[&>div]:h-dvh! [&>div]:w-[min(100vw,700px)]! [&>div]:rounded-none! [&>div]:flex [&>div]:flex-col',
                  '[&>div]:shadow-[1px_0_0_var(--color-border),-1px_0_0_var(--color-border),0_4px_16px_rgba(38,38,44,0.03)]!'
                )}
              >
                <div className="flex items-center gap-4 border-b border-gray-200 bg-background px-5 py-4">
                  <h4 className="m-0 flex-auto text-base font-medium leading-5 text-foreground">Permissions</h4>
                  <Button
                    type="button"
                    variant="subtle"
                    size="small"
                    icon={<Icon.Cross />}
                    onClick={cancelPermissionsPanel}
                  />
                </div>
                <Tabs
                  value={visibleSection}
                  onValueChange={(section) => setVisibleSection(section as Section)}
                  className="px-3 py-0.5 shadow-[0_1px_0_0_color-mix(in_oklch,var(--color-gray-950)_8%,transparent)] bg-gray-25 z-10"
                >
                  <TabsList variant="ghost" className="gap-0.5">
                    {apiKeySections.map((section) => (
                      <TabsTrigger key={section.value} value={section.value}>
                        {section.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                <div className="flex-auto overflow-y-auto [--page-margin:24px]">
                  <PermissionConfiguration
                    visibleSection={
                      visibleSection === Section.Transfers
                        ? PermissionConfigurationSection.Transfers
                        : PermissionConfigurationSection.Additional
                    }
                    excludeKeys={[...excludedAPIKeyPermissionKeys]}
                    fieldPathPrefix="permissions"
                    forcedMode={selectedMode}
                    includeAdditionalPermissionKeys={[...includedAPIKeyAdditionalPermissionKeys]}
                    onOffEnabledPermissionLevel="read"
                    sandboxEntitiesAndAccounts={sandboxEntitiesAndAccounts}
                    liveEntitiesAndAccounts={liveEntitiesAndAccounts}
                  />
                </div>
                <div className="box-border flex justify-between gap-5 border-t border-gray-200 bg-gray-25 px-6 py-5">
                  <Button type="button" variant="secondary" onClick={cancelPermissionsPanel}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={submitPermissionsPanel}>
                    Done
                  </Button>
                </div>
              </PopoverBase>
            )}
            {apiKeyPermissionsEnabled && apiKeyCanCreateTransfers && (
              <Dialog
                open={isApprovalsPanelOpen}
                onOpenChange={(isOpen) => {
                  if (!isOpen) {
                    cancelApprovalsPanel();
                  }
                }}
              >
                <DialogContent className="sm:max-w-sm">
                  <DialogHeader>
                    <DialogTitle>Transfer Approvals</DialogTitle>
                    <DialogDescription>
                      Select which transfer types sent by this key will require approval.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mb-2 grid overflow-hidden rounded-md border border-border [&_.approval-config-checkbox-cell_.form-field]:!grid-cols-[auto_1fr] [&_.approval-config-checkbox-cell_.form-field]:!items-center [&_.approval-config-checkbox-cell_.form-field]:!gap-x-2 [&_.approval-config-checkbox-cell_.form-field]:!gap-y-0 [&_.approval-config-checkbox-cell_.form-field>div:first-child]:order-2 [&_.approval-config-checkbox-cell_.form-field>div:nth-child(2)]:order-1">
                    <div
                      className="approval-config-checkbox-cell cursor-pointer border-b border-border p-3"
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleApprovalConfigField('approvalConfig.enabledForAchCredits')}
                      onKeyDown={(event) =>
                        handleApprovalConfigCellKeyDown(event, 'approvalConfig.enabledForAchCredits')
                      }
                    >
                      <FormField<APIKeyFormValues> id="approvalConfig.enabledForAchCredits" label="ACH Credits">
                        {({ value, onChange }) => (
                          <Checkbox
                            checked={value === true}
                            onClick={(event) => event.stopPropagation()}
                            onCheckedChange={(checked) => onChange(checked === true)}
                          />
                        )}
                      </FormField>
                    </div>
                    <div
                      className="approval-config-checkbox-cell cursor-pointer border-b border-border p-3"
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleApprovalConfigField('approvalConfig.enabledForAchDebits')}
                      onKeyDown={(event) =>
                        handleApprovalConfigCellKeyDown(event, 'approvalConfig.enabledForAchDebits')
                      }
                    >
                      <FormField<APIKeyFormValues> id="approvalConfig.enabledForAchDebits" label="ACH Debits">
                        {({ value, onChange }) => (
                          <Checkbox
                            checked={value === true}
                            onClick={(event) => event.stopPropagation()}
                            onCheckedChange={(checked) => onChange(checked === true)}
                          />
                        )}
                      </FormField>
                    </div>
                    <div
                      className="approval-config-checkbox-cell cursor-pointer p-3"
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleApprovalConfigField('approvalConfig.enabledForWire')}
                      onKeyDown={(event) => handleApprovalConfigCellKeyDown(event, 'approvalConfig.enabledForWire')}
                    >
                      <FormField<APIKeyFormValues> id="approvalConfig.enabledForWire" label="Wires">
                        {({ value, onChange }) => (
                          <Checkbox
                            checked={value === true}
                            onClick={(event) => event.stopPropagation()}
                            onCheckedChange={(checked) => onChange(checked === true)}
                          />
                        )}
                      </FormField>
                    </div>
                  </div>
                  <DialogFooter className="sm:justify-between">
                    <Button type="button" variant="secondary" onClick={cancelApprovalsPanel}>
                      Cancel
                    </Button>
                    <Button type="button" onClick={submitApprovalsPanel}>
                      Done
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </form>
        </FormProvider>
      </Loading>
    </>
  );
};
